// ── Chat Composer Overlay ──
// F-MOB-H3 (chat): touch-friendly chat composer for mobile.
// Invoked from the mobile HUD's CHAT button (or any future caller). Lifts a
// fullscreen-ish overlay with a text input + Send/Cancel buttons + an
// All/Team channel pill. The overlay autofocuses the input so the soft
// keyboard pops up immediately, and pauses TouchLookController so vertical
// keyboard drags don't tug the camera around.
//
// The composer does NOT speak the wire protocol directly — it delegates to
// an onSubmit(channel, text) callback so the host (game.ts) can route the
// message through the same path as the desktop ChatUI (which mirrors the
// message locally + sends a `chat` connection message).

import { CHAT_MAX_LENGTH, validateChatMessage } from './chat';
import { isMobile } from '../core/device';

export type ChatChannel = 'all' | 'team';

export interface ChatComposerOptions {
  /** Called when the user submits a non-empty message via Send or Enter. */
  onSubmit: (channel: ChatChannel, text: string) => void;
  /** Optional hook to pause/resume TouchLookController while the composer is
   *  open (so vertical keyboard drags don't tug the camera). */
  setTouchLookEnabled?: (enabled: boolean) => void;
  /** Optional player-name source for @mention autocomplete. */
  getPlayerNames?: () => readonly string[];
}

export class ChatComposer {
  private overlay: HTMLDivElement | null = null;
  private input: HTMLInputElement | null = null;
  private channelLabel: HTMLDivElement | null = null;
  private mentionList: HTMLDivElement | null = null;
  private mentionMatches: string[] = [];
  private mentionSelected = 0;
  private channel: ChatChannel = 'team';
  private isOpen = false;
  private opts: ChatComposerOptions;
  private boundKeydown: ((e: KeyboardEvent) => void) | null = null;

  constructor(opts: ChatComposerOptions) {
    this.opts = opts;
  }

  /** Open the composer for a given channel. Idempotent — calling open while
   *  already open re-routes to the new channel without re-creating DOM. */
  open(channel: ChatChannel): void {
    this.channel = channel;
    if (this.isOpen && this.overlay) {
      this.updateChannelLabel();
      return;
    }
    this.isOpen = true;
    this.buildOverlay();
    this.opts.setTouchLookEnabled?.(false);
  }

  /** Close the composer without submitting. Safe to call when already closed. */
  close(): void {
    if (!this.isOpen) return;
    this.isOpen = false;
    // F-UI-007: Tear down the document-level Escape listener so it never fires
    // against a torn-down overlay.
    if (this.boundKeydown) {
      document.removeEventListener('keydown', this.boundKeydown, true);
      this.boundKeydown = null;
    }
    if (this.overlay) {
      this.overlay.remove();
      this.overlay = null;
      this.input = null;
      this.channelLabel = null;
      this.mentionList = null;
      this.mentionMatches = [];
      this.mentionSelected = 0;
    }
    this.opts.setTouchLookEnabled?.(true);
  }

  /** True when the composer overlay is mounted and capturing input. */
  isVisible(): boolean {
    return this.isOpen;
  }

  /** Tear down the composer permanently (called on game-mode exit). */
  dispose(): void {
    this.close();
  }

  // ── private ──

  private buildOverlay(): void {
    const overlay = document.createElement('div') as HTMLDivElement;
    overlay.id = 'chat-composer-overlay';
    // BUG-HUD-11: on mobile, pin the composer to the bottom half of the
    // viewport so gameplay (and the joystick/buttons that the user is still
    // operating) remains visible behind the dimmer. Desktop keeps the full
    // overlay so the dimmer matches the page background.
    const mobile = isMobile();
    Object.assign(overlay.style, {
      position: 'fixed',
      left: '0',
      right: '0',
      bottom: '0',
      // Lift above the soft keyboard / safe-area insets. The on-screen
      // keyboard reflows the viewport on most browsers; bottom: 0 + dvh
      // keeps the composer pinned just above it.
      top: mobile ? 'auto' : '0',
      height: mobile ? '50vh' : '',
      background: 'rgba(0, 0, 0, 0.6)',
      zIndex: '120',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'flex-end',
      pointerEvents: 'auto',
      fontFamily: '"Courier New", monospace',
    });
    this.overlay = overlay;

    // Inner panel — sits on top of the dimmer, holds channel pill, input row.
    const panel = document.createElement('div');
    Object.assign(panel.style, {
      background: 'rgba(20, 20, 30, 0.95)',
      borderTop: '2px solid #e94560',
      padding: '12px 14px',
      paddingBottom: 'calc(12px + env(safe-area-inset-bottom, 0px))',
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
    });
    overlay.appendChild(panel);

    // Channel pill row — All / Team toggle. Tapping flips the channel.
    const channelRow = document.createElement('div');
    Object.assign(channelRow.style, {
      display: 'flex',
      gap: '6px',
      alignItems: 'center',
    });
    const channelLabel = document.createElement('div') as HTMLDivElement;
    channelLabel.id = 'chat-composer-channel-label';
    Object.assign(channelLabel.style, {
      fontSize: '12px',
      color: '#e8b849',
      fontWeight: 'bold',
      letterSpacing: '2px',
      textTransform: 'uppercase',
      padding: '4px 10px',
      border: '1px solid #e8b849',
      borderRadius: '3px',
      cursor: 'pointer',
      userSelect: 'none',
    });
    channelLabel.textContent = `[${this.channel.toUpperCase()}]`;
    channelLabel.addEventListener('click', () => {
      this.channel = this.channel === 'team' ? 'all' : 'team';
      this.updateChannelLabel();
    });
    channelRow.appendChild(channelLabel);
    panel.appendChild(channelRow);
    this.channelLabel = channelLabel;

    const mentionList = document.createElement('div') as HTMLDivElement;
    mentionList.id = 'chat-composer-mentions';
    Object.assign(mentionList.style, {
      display: 'none',
      flexDirection: 'column',
      gap: '2px',
      maxHeight: '128px',
      overflowY: 'auto',
      background: 'rgba(8, 8, 14, 0.96)',
      border: '1px solid rgba(233,69,96,0.35)',
      borderRadius: '4px',
      padding: '4px',
      color: '#ffffff',
    });
    panel.appendChild(mentionList);
    this.mentionList = mentionList;

    // Input row — text input + Send + Cancel.
    const inputRow = document.createElement('div');
    Object.assign(inputRow.style, {
      display: 'flex',
      gap: '6px',
      alignItems: 'center',
    });

    const input = document.createElement('input') as HTMLInputElement;
    input.id = 'chat-composer-input';
    input.type = 'text';
    input.maxLength = CHAT_MAX_LENGTH;
    input.placeholder = 'Type a message...';
    Object.assign(input.style, {
      flex: '1',
      padding: '10px 12px',
      fontSize: '15px',
      background: 'rgba(255,255,255,0.08)',
      border: '1px solid rgba(255,255,255,0.2)',
      borderRadius: '4px',
      color: '#ffffff',
      fontFamily: '"Courier New", monospace',
      outline: 'none',
    });
    input.addEventListener('keydown', (e: KeyboardEvent) => {
      // Always stop propagation so the key doesn't reach the global game
      // input handler (movement / fire / etc.).
      e.stopPropagation?.();
      if (this.handleMentionKey(e)) return;
      if (e.code === 'Enter' || e.key === 'Enter') {
        // On a single-line <input>, Shift+Enter cannot insert a newline, so
        // we always submit on Enter. If this element is ever swapped for a
        // <textarea>, Shift+Enter should fall through to the browser default
        // (insert newline) instead of submitting.
        const isTextArea =
          (this.input as unknown as { tagName?: string } | null)?.tagName === 'TEXTAREA';
        if (isTextArea && e.shiftKey) {
          // Let the browser insert a newline.
          return;
        }
        e.preventDefault?.();
        this.submit();
      } else if (e.code === 'Escape' || e.key === 'Escape') {
        e.preventDefault?.();
        this.close();
      }
    });
    input.addEventListener('input', () => this.updateMentionAutocomplete());
    inputRow.appendChild(input);
    this.input = input;

    // F-MOB-H3 (chat polish): On mobile the OS keyboard's submit key is not
    // always present (some Android keyboards hide the Send action, BT
    // keyboards on tablet route Enter differently). Give mobile a larger,
    // thumb-friendly SEND button next to the input so there is always an
    // unambiguous tap target. Desktop keeps the compact send button.
    const mobileSend = isMobile();
    const sendBtn = document.createElement('button') as HTMLButtonElement;
    sendBtn.id = 'chat-composer-send';
    sendBtn.textContent = 'Send';
    Object.assign(sendBtn.style, {
      padding: mobileSend ? '14px 22px' : '10px 16px',
      fontSize: mobileSend ? '15px' : '13px',
      fontWeight: 'bold',
      background: '#e94560',
      border: 'none',
      borderRadius: '4px',
      color: '#ffffff',
      cursor: 'pointer',
      letterSpacing: '1px',
      textTransform: 'uppercase',
      minWidth: mobileSend ? '72px' : '',
      minHeight: mobileSend ? '44px' : '',
      flexShrink: '0',
    });
    sendBtn.addEventListener('click', () => this.submit());
    inputRow.appendChild(sendBtn);

    const cancelBtn = document.createElement('button') as HTMLButtonElement;
    cancelBtn.id = 'chat-composer-cancel';
    cancelBtn.textContent = 'X';
    Object.assign(cancelBtn.style, {
      padding: '10px 14px',
      fontSize: '14px',
      fontWeight: 'bold',
      background: 'rgba(255,255,255,0.06)',
      border: '1px solid rgba(255,255,255,0.2)',
      borderRadius: '4px',
      color: '#cccccc',
      cursor: 'pointer',
    });
    cancelBtn.addEventListener('click', () => this.close());
    inputRow.appendChild(cancelBtn);

    panel.appendChild(inputRow);

    document.body.appendChild(overlay);

    // F-UI-007: Document-level capture-phase Escape so the composer closes
    // even when focus has shifted off the <input> (e.g. user tapped the
    // channel pill or the BT keyboard sends Escape with no focused field).
    this.boundKeydown = (e: KeyboardEvent) => {
      if (!this.isOpen) return;
      if (e.code === 'Escape' || e.key === 'Escape') {
        e.preventDefault?.();
        e.stopPropagation?.();
        this.close();
      }
    };
    document.addEventListener('keydown', this.boundKeydown, true);

    // Focus on next frame so the browser commits the DOM insertion before
    // the soft keyboard tries to tie itself to the input.
    try {
      const raf = (globalThis as { requestAnimationFrame?: (cb: () => void) => number }).requestAnimationFrame;
      if (typeof raf === 'function') {
        raf(() => input.focus?.());
      } else {
        input.focus?.();
      }
    } catch {
      input.focus?.();
    }
    // Some test environments resolve focus synchronously without rAF — call
    // focus directly as well so tests don't have to drive the rAF loop.
    input.focus?.();
  }

  private getActiveMentionQuery(): { start: number; end: number; query: string } | null {
    if (!this.input || !this.opts.getPlayerNames) return null;
    const value = this.input.value;
    const end = this.input.selectionStart ?? value.length;
    const beforeCursor = value.slice(0, end);
    const start = beforeCursor.lastIndexOf('@');
    if (start < 0) return null;
    if (start > 0 && !/\s/.test(beforeCursor[start - 1])) return null;

    const query = beforeCursor.slice(start + 1);
    if (/\s/.test(query)) return null;
    return { start, end, query };
  }

  private updateMentionAutocomplete(): void {
    if (!this.mentionList) return;
    const active = this.getActiveMentionQuery();
    if (!active) {
      this.hideMentionAutocomplete();
      return;
    }

    const query = active.query.toLowerCase();
    const names = this.opts.getPlayerNames?.() ?? [];
    this.mentionMatches = Array.from(new Set(names))
      .filter((name) => name.trim().length > 0)
      .filter((name) => name.toLowerCase().includes(query))
      .slice(0, 6);

    if (this.mentionMatches.length === 0) {
      this.hideMentionAutocomplete();
      return;
    }

    this.mentionSelected = Math.min(this.mentionSelected, this.mentionMatches.length - 1);
    this.renderMentionAutocomplete();
  }

  private renderMentionAutocomplete(): void {
    if (!this.mentionList) return;
    this.mentionList.textContent = '';
    this.mentionList.style.display = 'flex';

    this.mentionMatches.forEach((name, index) => {
      const row = document.createElement('div');
      Object.assign(row.style, {
        padding: '6px 8px',
        borderRadius: '3px',
        background: index === this.mentionSelected ? 'rgba(233,69,96,0.32)' : 'transparent',
        color: '#ffffff',
        cursor: 'pointer',
        fontSize: '12px',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      });
      row.textContent = `@${name}`;
      row.addEventListener('mousedown', (event) => {
        event.preventDefault();
        this.mentionSelected = index;
        this.applySelectedMention();
      });
      this.mentionList!.appendChild(row);
    });
  }

  private hideMentionAutocomplete(): void {
    this.mentionMatches = [];
    this.mentionSelected = 0;
    if (this.mentionList) {
      this.mentionList.style.display = 'none';
      this.mentionList.textContent = '';
    }
  }

  private handleMentionKey(e: KeyboardEvent): boolean {
    if (!this.mentionList || this.mentionList.style.display === 'none' || this.mentionMatches.length === 0) {
      return false;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault?.();
      this.mentionSelected = (this.mentionSelected + 1) % this.mentionMatches.length;
      this.renderMentionAutocomplete();
      return true;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault?.();
      this.mentionSelected = (this.mentionSelected - 1 + this.mentionMatches.length) % this.mentionMatches.length;
      this.renderMentionAutocomplete();
      return true;
    }
    if (e.key === 'Tab' || e.key === 'Enter' || e.code === 'Enter') {
      e.preventDefault?.();
      this.applySelectedMention();
      return true;
    }

    return false;
  }

  private applySelectedMention(): void {
    if (!this.input) return;
    const active = this.getActiveMentionQuery();
    const name = this.mentionMatches[this.mentionSelected];
    if (!active || !name) return;

    const value = this.input.value;
    const replacement = `@${name} `;
    this.input.value = value.slice(0, active.start) + replacement + value.slice(active.end);
    const cursor = active.start + replacement.length;
    this.input.setSelectionRange?.(cursor, cursor);
    this.hideMentionAutocomplete();
    this.input.focus?.();
  }

  private updateChannelLabel(): void {
    if (!this.channelLabel) return;
    this.channelLabel.textContent = `[${this.channel.toUpperCase()}]`;
    this.channelLabel.style.color = this.channel === 'team' ? '#e8b849' : '#cccccc';
    this.channelLabel.style.borderColor = this.channel === 'team' ? '#e8b849' : '#cccccc';
  }

  private submit(): void {
    if (!this.input) return;
    const raw = this.input.value ?? '';
    const text = raw.trim();
    const v = validateChatMessage(text);
    if (!v.valid) {
      // Empty / overlong — drop silently and let the user keep typing. No
      // onSubmit fired and the overlay stays open. The desktop chat closes
      // on empty Enter; here we keep open because the soft keyboard is
      // expensive to re-summon on mobile.
      return;
    }
    this.opts.onSubmit(this.channel, text);
    this.close();
  }
}
